import * as React from 'react';
import { v4 as uuid } from 'uuid';

import { VoiceboxConversationContextValue } from 'bones/constants/voicebox/conversation';
import {
  VoiceboxApp,
  VoiceboxMessage,
  VoiceboxRequestMessage,
  VoiceboxResponse,
  VoiceboxResponseAction,
  VoiceboxResponseActionType,
  VoiceboxResponseMessage,
  VoiceboxResponseMessageType,
} from 'bones/constants/voicebox/message';
import { VoiceboxSchema } from 'bones/constants/voicebox/schema';
import { VoiceboxSettings } from 'bones/constants/voicebox/settings';
import { useDidValueChange } from 'bones/utils/hooks/useDidValueChange';
import { useIsMounted } from 'bones/utils/hooks/useIsMounted';
import { usePrevious } from 'bones/utils/hooks/usePrevious';
import { noop } from 'bones/utils/noop';
import {
  getFuzzySeededValue,
  getSeededVoiceboxResponseMessage,
  getSeededVoiceboxResponseSparql,
} from 'bones/utils/voicebox/getSeededVoicebox';

export interface StaticVoiceboxProps {
  /**
   * the unique key for managing multiple instances of voicebox,
   * i.e. the Studio note id
   */
  id?: string | null;
  /**
   * a unique key for manually triggering a state refresh
   */
  key?: string | null;
  initialConversationId?: string;
  /**
   * a set of initial request and response messages loaded from an external source,
   * will become obsolete once we can query the conversation history from the API
   */
  initialMessages?: VoiceboxMessage[];
  loadingConversation?: boolean;
  loadingDownloadMessageId?: string;
  onDownloadCsv?: (query: string, maybeSettings?: VoiceboxSettings) => void;
  onFeedback?: (score?: number, comment?: string) => void;
  /**
   * @param err: the error message
   * @param id the unique key for managing multiple instances of voicebox,
   * i.e. the Studio note id
   */
  onMessageError?: (err: string, id: string | null) => void;
  /**
   * @param message: the voicebox response message
   * @param id the unique key for managing multiple instances of voicebox,
   * i.e. the Studio note id
   */
  onMessageSuccess?: (
    message: VoiceboxResponseMessage,
    id: string | null
  ) => void;
  cloudResponseConfig?: {
    responseMessages?: Record<string, string>;
    responseSparqlQuery?: (query: string) => string;
    responseSparqlQueries?: Record<string, string>;
    responseAdditionalActions?: Record<string, VoiceboxResponseAction[]>;
    responseReasoningActions?: Record<string, VoiceboxResponseAction[]>;
  };
  studioResponseConfig?: {
    responseMessage: (query: string) => string;
    responseSparqlQueries?: Record<string, string>;
  };
  schema: VoiceboxSchema;
  staticMessages?: VoiceboxMessage[];
}

export const useStaticVoicebox = ({
  id = null,
  initialConversationId,
  initialMessages = [],
  key = null,
  loadingConversation,
  loadingDownloadMessageId = '',
  onDownloadCsv = noop,
  onFeedback = noop,
  onMessageError = noop,
  onMessageSuccess = noop,
  cloudResponseConfig,
  studioResponseConfig,
  schema,
  staticMessages = [],
}: StaticVoiceboxProps): VoiceboxConversationContextValue => {
  const isMounted = useIsMounted();
  const didIdChange = useDidValueChange(id);
  const didKeyChange = useDidValueChange(key);

  const didInitialConversationIdChange = useDidValueChange(
    initialConversationId
  );

  const prevConnectionId = usePrevious(schema.config.connectionId);
  const didConnectionIdChange = useDidValueChange(schema.config.connectionId);

  const [conversationId, setConversationId] = React.useState(
    () => initialConversationId || ''
  );
  const [isLoadingFeedback, setIsLoadingFeedback] = React.useState(false);
  const [isLoadingMessage, setIsLoadingMessage] = React.useState(false);
  const [messages, setMessages] =
    React.useState<VoiceboxMessage[]>(initialMessages);

  // reset the messages and conversation id when the id, connection, or conversation changes
  React.useEffect(() => {
    if (
      didIdChange ||
      didKeyChange ||
      (prevConnectionId !== undefined && didConnectionIdChange) ||
      (didInitialConversationIdChange &&
        initialConversationId &&
        initialConversationId !== conversationId)
    ) {
      setIsLoadingFeedback(false);
      setIsLoadingMessage(false);
      setConversationId(initialConversationId || '');
      setMessages(initialMessages);
    }
  }, [
    conversationId,
    didConnectionIdChange,
    didIdChange,
    didInitialConversationIdChange,
    didKeyChange,
    initialConversationId,
    initialMessages,
    prevConnectionId,
  ]);

  const idRef = React.useRef(id);
  React.useEffect(() => {
    idRef.current = id;
  }, [id]);

  const handleDownloadCsv = React.useCallback(
    async (
      message: VoiceboxResponseMessage,
      maybeSettings?: VoiceboxSettings
    ): Promise<boolean> => {
      const sparqlAction = message.actions?.find(
        ({ type }) => type === VoiceboxResponseActionType.SPARQL
      );

      if (!sparqlAction) {
        return false;
      }

      onDownloadCsv(sparqlAction.value, maybeSettings);
      return true;
    },
    [onDownloadCsv]
  );

  const handleSendFeedback = React.useCallback(
    async (
      message: VoiceboxResponseMessage,
      feedbackScore?: number,
      feedbackComment?: string
    ): Promise<VoiceboxResponse> => {
      setIsLoadingFeedback(true);

      if (feedbackComment === '__TIMEOUT__') {
        return new Promise<VoiceboxResponse>(() => {});
      }

      return new Promise<VoiceboxResponse>((res) => {
        setTimeout(() => {
          const isFailure = feedbackComment === '__FAILURE__';
          const newMessage: VoiceboxResponse['newMessage'] = isFailure
            ? undefined
            : { ...message, feedbackScore };

          if (!isMounted.current || id !== idRef.current) {
            res({ id, newMessage, succeeded: true });
            return;
          }

          if (isFailure) {
            setIsLoadingFeedback(false);
            res({ id, succeeded: false });
            return;
          }

          setMessages((prevMessages) =>
            prevMessages.map((prevMessage) =>
              prevMessage.messageId !== message.messageId
                ? prevMessage
                : { ...prevMessage, feedbackScore }
            )
          );

          setIsLoadingFeedback(false);
          onFeedback(feedbackScore, feedbackComment);
          res({ id, newMessage, succeeded: true });
        }, 1000);
      });
    },
    [id, isMounted, onFeedback]
  );

  const handleSendMessage = React.useCallback(
    async (
      query: string,
      maybeSettings?: VoiceboxSettings,
      aiReasoning?: boolean
    ): Promise<VoiceboxResponse> => {
      const newConversationId = conversationId || uuid();
      setIsLoadingMessage(true);
      setConversationId(newConversationId);

      const settings =
        maybeSettings ||
        schema.config.initialSettings ||
        schema.config.defaultSettings;
      const requestMessage: VoiceboxRequestMessage = {
        app: schema.config.app,
        connectionId: schema.config.connectionId,
        context:
          schema.config.app === VoiceboxApp.DESIGNER
            ? {
                ...schema.config.context,
              }
            : {
                ...schema.config.context,
                database_id: settings.databaseId,
                model: settings.model,
                named_graphs: settings.namedGraphs,
                reasoning: settings.reasoning,
              },
        conversationId,
        messageId: uuid(),
        query,
        isRequest: true,
      };
      setMessages((prevMessages) => [...prevMessages, requestMessage]);

      if (query === '__TIMEOUT__') {
        return new Promise<VoiceboxResponse>(() => {});
      }

      let result: string | undefined;
      let sparql: string | undefined;
      let reasoningActions: VoiceboxResponseAction[] | undefined;
      let additionalActions: VoiceboxResponseAction[] | undefined;
      if (cloudResponseConfig) {
        // hard-coded response message with optional sparql query
        result = getSeededVoiceboxResponseMessage(
          cloudResponseConfig.responseMessages || {},
          query
        );
        sparql =
          getSeededVoiceboxResponseSparql(
            cloudResponseConfig.responseSparqlQueries || {},
            query
          ) || cloudResponseConfig.responseSparqlQuery?.(query);
        reasoningActions = getFuzzySeededValue(
          cloudResponseConfig.responseReasoningActions || {},
          query
        );
        additionalActions = getFuzzySeededValue(
          cloudResponseConfig.responseAdditionalActions || {},
          query
        );
      } else if (studioResponseConfig) {
        // hard-coded sparql query and a constant response messge
        sparql = getSeededVoiceboxResponseSparql(
          studioResponseConfig.responseSparqlQueries || {},
          query
        );
        if (sparql) {
          result = studioResponseConfig.responseMessage(query);
        }
      }

      return new Promise<VoiceboxResponse>((res) => {
        setTimeout(
          () => {
            let isFailure = query === '__FAILURE__';

            const newMessage: VoiceboxResponse['newMessage'] = {
              actions: undefined,
              conversationId,
              messageId: uuid(),
              result: schema.copy.errors.failureWithError(
                query,
                schema.copy.errors.unknownError
              ),
              success: !isFailure,
              type: isFailure
                ? VoiceboxResponseMessageType.FAILURE
                : VoiceboxResponseMessageType.ANSWER,
              isResponse: true,
            };

            if (!isFailure) {
              newMessage.conversationId = newConversationId;

              if (reasoningActions) {
                newMessage.actions = reasoningActions;
                newMessage.pending = true;
                newMessage.result = '';
                newMessage.type = undefined;
              } else if (result) {
                newMessage.result = result;
                if (sparql) {
                  newMessage.actions = [
                    { type: VoiceboxResponseActionType.SPARQL, value: sparql },
                  ];
                }
                if (additionalActions) {
                  newMessage.actions = [
                    ...(newMessage.actions || []),
                    ...additionalActions,
                  ];
                }
              } else {
                // could not find response message or sparql query
                isFailure = true;
                newMessage.result = schema.copy.errors.failure(query);
                newMessage.success = false;
                newMessage.type = VoiceboxResponseMessageType.FAILURE;
              }
            }

            if (isFailure) {
              onMessageError(newMessage.result, id);
            }

            if (
              (!isMounted.current || id !== idRef.current) &&
              !newMessage.pending
            ) {
              res({ id, newMessage, succeeded: true });
              return;
            }

            setMessages((prevMessages) => [...prevMessages, newMessage]);

            if (!newMessage.pending) {
              setIsLoadingMessage(Boolean(newMessage.pending));
              onMessageSuccess(newMessage, id);
              res({ id, newMessage, succeeded: !isFailure });
            }
          },
          aiReasoning ? 0 : 2000
        );
      });
    },
    [
      cloudResponseConfig,
      conversationId,
      id,
      isMounted,
      onMessageError,
      onMessageSuccess,
      schema,
      studioResponseConfig,
    ]
  );

  const allMessages = React.useMemo(
    () => staticMessages.concat(messages),
    [messages, staticMessages]
  );

  return {
    conversationId,
    id,
    isLoadingConversation: Boolean(
      loadingConversation && !initialMessages.length
    ),
    isLoadingInitialConversation: Boolean(
      loadingConversation && !initialMessages.length
    ),
    isLoadingFeedback,
    isLoadingMessage,
    isRefetchingConversation: Boolean(
      loadingConversation && initialMessages.length
    ),
    loadingDownloadMessageId,
    messages: allMessages,
    downloadCsv: handleDownloadCsv,
    sendFeedback: handleSendFeedback,
    sendMessage: handleSendMessage,
  };
};
